import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ArchivedView, ListOwnership } from "@/enum/list.enum";
import { useSearch } from "@/utils";

import { ALL_SOURCES } from "../../List/components/SourceTypePalette";
import { getSourceFilterEntry } from "../../List/config/sourceTypeFilterCatalog";

import { CampaignChannelFilter, CampaignStatusFilter } from "../utils/campaignFilters";
import {
	CampaignPinnedFilterTab,
	CampaignPinnedTabFilters,
	areCampaignPinnedFiltersEqual,
	findMatchingCampaignPinnedTabId,
	isCampaignFilterPinnable,
	normalizeCampaignSourceType,
} from "../utils/campaignPinnedFilterTabs";
import { useCampaignsPinnedTabs } from "./useCampaignsPinnedTabs";

import type { PinSummaryItem } from "../../List/components/PinFilterButton";

export type CampaignsFiltersController = ReturnType<typeof useCampaignsFilters>;

/**
 * Owns every filter axis of the Campaigns overview plus the pin-to-tab reconciliation on top of
 * it (PREC-3880).
 *
 * It lives above both `CampaignsOwnershipTabs` (which renders the pinned chips) and
 * `CampaignsTable` (which renders the filter bar and runs the query) because a pinned tab restores
 * *ownership and filters together* — state split across those two components could not do that.
 * Keeping it here also keeps `CampaignsTable` under the 500-line ratchet.
 *
 */
export const useCampaignsFilters = ({
	initialOwnership = ListOwnership.MINE,
	onFiltersRestored,
}: {
	/** Starting scope. Overridable so tests (and future deep links) can open on Team / All. */
	initialOwnership?: ListOwnership;
	/** Called after a pinned tab is applied, so the table can reset paging. */
	onFiltersRestored?: () => void;
} = {}) => {
	const { t } = useTranslation();
	const tc = (key: string) => t(`campaignsOverview.${key}`);

	const { searchValue, setSearchValue, query, setQuery } = useSearch();
	const [ownership, setOwnership] = useState<ListOwnership>(initialOwnership);
	const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>(CampaignStatusFilter.ALL);
	const [channelFilter, setChannelFilter] = useState<CampaignChannelFilter>(
		CampaignChannelFilter.ALL
	);
	const [archivedView, setArchivedView] = useState<ArchivedView>(ArchivedView.HIDE);
	const [selectedSourceType, setSelectedSourceType] = useState<string>(ALL_SOURCES);

	const { pinnedTabs, addPinnedTab, renamePinnedTab, updatePinnedTabFilters, removePinnedTab } =
		useCampaignsPinnedTabs();

	// --- Snapshot the current view, then reconcile it against the user's pinned tabs. ---
	const normalizedSourceType = normalizeCampaignSourceType(selectedSourceType);
	const searchTerm = searchValue.trim();

	const currentFilters: CampaignPinnedTabFilters = {
		ownership,
		statusFilter,
		channelFilter,
		archivedView,
		sourceType: normalizedSourceType,
		search: searchTerm || undefined,
	};

	const activePinnedTabId = findMatchingCampaignPinnedTabId(pinnedTabs, currentFilters) ?? null;
	const isPinnable = isCampaignFilterPinnable(currentFilters);
	/**
	 * Whether "Pin to tab" should be offered at all. A view that is *already* pinned is worth
	 * pinning in the abstract but must not be pinned again — the second row would be a
	 * byte-identical twin the user cannot tell apart in the strip, and cannot meaningfully choose
	 * between. `activePinnedTabId` already knows the current view is one of the saved tabs.
	 */
	const canPin = isPinnable && activePinnedTabId === null;

	const sourceTypeLabel = (): string => {
		const entry = normalizedSourceType ? getSourceFilterEntry(normalizedSourceType) : undefined;

		return entry ? t(entry.labelKey) : t("list.sourceFilter.all");
	};

	const pinSummaryItems: PinSummaryItem[] = [
		...(searchTerm ? [{ label: t("list.pinTab.criteria.search"), value: searchTerm }] : []),
		...(ownership !== ListOwnership.MINE
			? [{ label: t("list.pinTab.criteria.scope"), value: tc(`tabs.${ownership.toLowerCase()}`) }]
			: []),
		...(statusFilter !== CampaignStatusFilter.ALL
			? [{ label: tc("pinTab.criteria.status"), value: tc(`statusFilter.${statusFilter}`) }]
			: []),
		...(channelFilter !== CampaignChannelFilter.ALL
			? [{ label: tc("pinTab.criteria.channel"), value: tc(`channelFilter.${channelFilter}`) }]
			: []),
		...(normalizedSourceType
			? [{ label: t("list.pinTab.criteria.source"), value: sourceTypeLabel() }]
			: []),
		...(archivedView !== ArchivedView.HIDE
			? [
					{
						label: t("list.pinTab.criteria.archived"),
						value: t("list.pinTab.criteria.archivedValue"),
					},
				]
			: []),
	];

	const suggestedPinName = pinSummaryItems.length
		? pinSummaryItems.map((item) => item.value).join(" · ")
		: t("list.pinTab.defaultName");

	const applyPinnedFilters = (filters: CampaignPinnedTabFilters) => {
		const restoredSearch = filters.search?.trim() ?? "";

		setOwnership(filters.ownership);
		setStatusFilter(filters.statusFilter);
		setChannelFilter(filters.channelFilter);
		setArchivedView(filters.archivedView);
		setSelectedSourceType(filters.sourceType ?? ALL_SOURCES);
		setSearchValue(restoredSearch);
		// Apply the term to the *debounced* query immediately so the first fetch after the click is
		// already the filtered one. Relying on the 800ms debounce would fire an intermediate
		// unfiltered fetch that can resolve last and clobber the restored results.
		setQuery(restoredSearch);

		onFiltersRestored?.();
	};

	return {
		// Filter state.
		ownership,
		setOwnership,
		statusFilter,
		setStatusFilter,
		channelFilter,
		setChannelFilter,
		archivedView,
		setArchivedView,
		selectedSourceType,
		setSelectedSourceType,
		searchValue,
		setSearchValue,
		query,

		// Pin-to-tab.
		pinnedTabs,
		activePinnedTabId,
		isPinnable,
		canPin,
		pinSummaryItems,
		suggestedPinName,
		currentFilters,
		applyPinnedFilters,
		addPinnedTab,
		renamePinnedTab,
		updatePinnedTabFilters,
		removePinnedTab,
		/** True when the tab already holds exactly the current view, so "update" would be a no-op. */
		isPinnedTabUpToDate: (tab: CampaignPinnedFilterTab) =>
			areCampaignPinnedFiltersEqual(tab.filters, currentFilters),
	};
};
